import { useEffect } from 'react';
import type { RefObject } from 'react';

export type EllieMarkerRefs = {
  svg: RefObject<SVGSVGElement | null>;
  curve: RefObject<SVGPathElement | null>;
  line: RefObject<SVGLineElement | null>;
  dot: RefObject<SVGCircleElement | null>;
  label: RefObject<SVGTextElement | null>;
  pillRect: RefObject<SVGRectElement | null>;
  pillDot: RefObject<SVGCircleElement | null>;
  pillText: RefObject<SVGTextElement | null>;
};

/** Binary search along the curve for the y at a given x — mockup `sampleY` (L1139–1147). */
const sampleY = (path: SVGPathElement, targetX: number): number => {
  const total = path.getTotalLength();
  let lo = 0;
  let hi = total;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (path.getPointAtLength(mid).x < targetX) lo = mid;
    else hi = mid;
  }
  return path.getPointAtLength((lo + hi) / 2).y;
};

const textLength = (node: SVGTextElement): number => {
  try {
    return typeof node.getComputedTextLength === 'function' ? node.getComputedTextLength() : 0;
  } catch {
    return 0;
  }
};

/**
 * Drives the "Ellie" marker on the distribution chart from page scroll — a port of the
 * mockup's `updateEllie` (L1149–1199) and its `componentDidMount` wiring (L1068–1085).
 *
 * Deliberately mutates SVG attributes through refs rather than React state: this is a
 * per-frame DOM measurement/animation, not renderable state.
 */
const useEllieMarker = (refs: EllieMarkerRefs): void => {
  useEffect(() => {
    const updateEllie = (): void => {
      const curve = refs.curve.current;
      const line = refs.line.current;
      const dot = refs.dot.current;
      const label = refs.label.current;
      if (!curve || !line || !dot || !label) return;
      const svg = refs.svg.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const ih = window.innerHeight || 800;
      let p = (ih * 0.82 - rect.top) / (ih * 0.5);
      p = Math.max(0, Math.min(1, p));
      const sessions = Math.round(1 + p * 71);
      const x = 40 + (sessions / 112) * 480;

      // jsdom has no SVG geometry: fall back to the markup's static y.
      let y = 82;
      try {
        y = sampleY(curve, x);
      } catch {
        y = 82;
      }
      if (!Number.isFinite(y)) y = 82;

      line.setAttribute('x1', String(x));
      line.setAttribute('x2', String(x));
      dot.setAttribute('cx', String(x));
      dot.setAttribute('cy', y.toFixed(1));

      const annX = Math.max(56, Math.min(504, x));
      const engaged = p >= 0.5;
      label.setAttribute('text-anchor', 'start');
      label.textContent = `Ellie · ${sessions}`;

      const pillRect = refs.pillRect.current;
      const pillText = refs.pillText.current;
      const pillDot = refs.pillDot.current;

      let lw = textLength(label);
      if (lw < 8) lw = 8.2 * `Ellie · ${sessions}`.length;

      if (pillRect && pillText && pillDot) {
        pillText.textContent = engaged ? 'ENGAGED' : 'UNENGAGED';
        let tw = textLength(pillText);
        if (tw < 8) tw = 7.4 * (engaged ? 7 : 9);
        const dotR = 4.5;
        const padL = 12;
        const dotGap = 7;
        const padR = 13;
        const pillW = padL + dotR * 2 + dotGap + tw + padR;
        const gap = 11;
        const totalW = lw + gap + pillW;
        let startX = annX - totalW / 2;
        startX = Math.max(6, Math.min(560 - 6 - totalW, startX));
        label.setAttribute('x', String(startX));
        const pillX = startX + lw + gap;
        pillRect.setAttribute('x', String(pillX));
        pillRect.setAttribute('width', String(pillW));
        pillRect.setAttribute('fill', engaged ? '#DCFCE7' : '#FEE2E2');
        pillDot.setAttribute('cx', String(pillX + padL + dotR));
        pillDot.setAttribute('fill', engaged ? '#16A34A' : '#DC2626');
        pillText.setAttribute('x', String(pillX + padL + dotR * 2 + dotGap));
        pillText.setAttribute('fill', engaged ? '#166534' : '#B91C1C');
      } else {
        label.setAttribute('x', String(annX));
      }
    };

    let raf: number | null = null;
    let cancelled = false;
    const safeUpdate = (): void => {
      if (!cancelled) updateEllie();
    };
    const onScroll = (): void => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        safeUpdate();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    safeUpdate();
    // The Bebas Neue metrics settle late; re-measure as the page and fonts land.
    const timers = [80, 600, 1500].map((ms) => window.setTimeout(safeUpdate, ms));
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(safeUpdate).catch(() => undefined);
      try {
        document.fonts.load("16px 'Bebas Neue'").then(safeUpdate).catch(() => undefined);
      } catch {
        /* font loading unsupported — the timers above still re-measure */
      }
    }

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      timers.forEach((timer) => window.clearTimeout(timer));
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [refs]);
};

export default useEllieMarker;
